import { useState } from 'react';
import type { DayAvailabilityStatus } from '@/services/bookingService';
import './BookingCalendar.css';

interface BookingCalendarProps {
  selectedDate: string;
  onSelectDate: (date: string) => void;
  minDate?: string;
  dayStatuses?: DayAvailabilityStatus[];
  onMonthChange?: (year: number, month: number) => void;
}

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

function toDateString(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function BookingCalendar({
  selectedDate,
  onSelectDate,
  minDate,
  dayStatuses = [],
  onMonthChange,
}: BookingCalendarProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const startOffset = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const statusMap = new Map<string, DayAvailabilityStatus['status']>();
  for (const ds of dayStatuses) {
    statusMap.set(ds.date, ds.status);
  }

  const prevMonth = () => {
    const newMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const newYear  = viewMonth === 0 ? viewYear - 1 : viewYear;
    setViewMonth(newMonth);
    setViewYear(newYear);
    onMonthChange?.(newYear, newMonth);
  };

  const nextMonth = () => {
    const newMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const newYear  = viewMonth === 11 ? viewYear + 1 : viewYear;
    setViewMonth(newMonth);
    setViewYear(newYear);
    onMonthChange?.(newYear, newMonth);
  };

  const isBeforeMin = (dateStr: string) => {
    if (!minDate) return false;
    return dateStr < minDate;
  };

  const isToday = (dateStr: string) => {
    return dateStr === toDateString(today.getFullYear(), today.getMonth(), today.getDate());
  };

  const cells: Array<{ day: number | null; dateStr: string | null }> = [];
  for (let i = 0; i < startOffset; i++) cells.push({ day: null, dateStr: null });
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: toDateString(viewYear, viewMonth, d) });
  }
  while (cells.length % 7 !== 0) cells.push({ day: null, dateStr: null });

  const canGoPrev =
    viewYear > today.getFullYear() ||
    (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  const hasAvailabilityData = dayStatuses.length > 0;

  return (
    <div className="cal-root">
      {/* Month navigation */}
      <div className="cal-nav">
        <button
          type="button"
          onClick={prevMonth}
          disabled={!canGoPrev}
          className="cal-nav-btn"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>

        <div className="text-center">
          <span className="cal-month-label">{MONTHS[viewMonth]}</span>
          <span className="cal-year-label">{viewYear}</span>
        </div>

        <button
          type="button"
          onClick={nextMonth}
          className="cal-nav-btn"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="cal-weekdays">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="cal-weekday">{wd}</div>
        ))}
      </div>

      {/* Day cells */}
      <div className="cal-grid">
        {cells.map((cell, idx) => {
          if (!cell.day || !cell.dateStr) {
            return <div key={`empty-${idx}`} className="cal-cell-empty" />;
          }

          const isPast     = isBeforeMin(cell.dateStr);
          const selected   = cell.dateStr === selectedDate;
          const todayCell  = isToday(cell.dateStr);
          const dayStatus  = statusMap.get(cell.dateStr);
          const isOff      = !isPast && dayStatus === 'off';
          const isBlocked  = !isPast && dayStatus === 'blocked';
          const isDisabled = isPast || isOff || isBlocked;

          let stateClass = '';
          if (selected)       stateClass = 'cal-day--selected';
          else if (isPast)    stateClass = 'cal-day--past';
          else if (isBlocked) stateClass = 'cal-day--blocked';
          else if (isOff)     stateClass = 'cal-day--off';
          else if (todayCell) stateClass = 'cal-day--today';
          else                stateClass = 'cal-day--available';

          return (
            <button
              key={cell.dateStr}
              type="button"
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelectDate(cell.dateStr!)}
              title={isBlocked ? 'Bloqueado' : isOff ? 'Día no laborable' : undefined}
              className={`cal-day ${stateClass}`}
            >
              {cell.day}
              {isBlocked && !isPast && <span className="cal-day-dot" />}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="cal-legend">
        <div className="cal-legend-item">
          <div className="cal-legend-dot cal-legend-dot--today" />
          <span className="cal-legend-text">Hoy</span>
        </div>
        <div className="cal-legend-item">
          <div className="cal-legend-dot cal-legend-dot--selected" />
          <span className="cal-legend-text">Seleccionado</span>
        </div>
        {hasAvailabilityData && (
          <>
            <div className="cal-legend-item">
              <div className="cal-legend-dot cal-legend-dot--off" />
              <span className="cal-legend-text">Día libre</span>
            </div>
            <div className="cal-legend-item">
              <div className="cal-legend-dot cal-legend-dot--blocked" />
              <span className="cal-legend-text">Bloqueado</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}